import React, { FC, useState, useEffect, useRef } from 'react';
import { Modal } from 'antd';
import type { TrainerPackage } from 'src/api/services/packagesService';
import { ReactComponent as CalendarPeriodIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/create-edit-package/calendar-period.svg';
import { ReactComponent as AttachedDocsIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-offer/attached-docs.svg';
import { ReactComponent as SendOfferIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-offer/send-offer.svg';
import { ReactComponent as WhatsIncludedIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-offer/whats-included.svg';
import { ReactComponent as DropdownIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-offer/dropdown.svg';
import { ReactComponent as CloseIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-offer/close.svg';
import { ReactComponent as ChevronRightIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/create-edit-package/chevron-right.svg';
import './SendFinalOfferModal.scss';

export const DEFAULT_WHATS_INCLUDED = [
  'Pre-session questionnaire to understand your goals, fitness level, and schedule',
  'Session programme tailored to you, integrating AI-based form tracking & MPT Score',
  'Real-time feedback from a certified PT',
  'Session-specific MPT Score (accuracy, goal-based calorie burn, reps completed)',
  'Access to personal trainer notes & AI session history',
  'Chat/message support in the app for quick form checks or questions (during package period)',
];

interface SendFinalOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  publishedPackages: TrainerPackage[];
  initialPackageId?: string;
  initialStartDate?: string;
  onConfirmSend: (offerData: {
    pkg: TrainerPackage;
    startDate: string;
    whatsIncluded: string[];
  }) => void;
  isSending?: boolean;
  onOpenDocument?: (docType: string) => void;
}

export const SendFinalOfferModal: FC<SendFinalOfferModalProps> = ({
  isOpen,
  onClose,
  publishedPackages,
  initialPackageId,
  initialStartDate,
  onConfirmSend,
  isSending = false,
  onOpenDocument,
}) => {
  const [selectedPackage, setSelectedPackage] = useState<TrainerPackage | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [startDate, setStartDate] = useState<string>(initialStartDate || '28 Sep 2026');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialStartDate) {
      setStartDate(initialStartDate);
    }
  }, [initialStartDate]);

  // Sync selected package from published list
  useEffect(() => {
    if (publishedPackages.length > 0) {
      const matched = publishedPackages.find((p) => p.id === initialPackageId);
      setSelectedPackage(matched || publishedPackages[0]);
    } else {
      setSelectedPackage(null);
    }
  }, [publishedPackages, initialPackageId]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  if (!isOpen) return null;

  const pkgName = selectedPackage?.name || 'Basic Package';
  const price = selectedPackage?.price !== undefined ? selectedPackage.price : 95;
  const currencySymbol =
    selectedPackage?.currencySymbol || (selectedPackage?.currency === 'GBP' ? '£' : '$');
  const duration = selectedPackage?.duration || '4 weeks';

  const handleSend = () => {
    if (!selectedPackage) return;
    onConfirmSend({
      pkg: selectedPackage,
      startDate,
      whatsIncluded: DEFAULT_WHATS_INCLUDED,
    });
  };

  const handleDocClick = (docType: string) => {
    if (onOpenDocument) {
      onOpenDocument(docType);
    } else {
      window.open(
        `/onboarding-trainer/dashboard/profile?tab=packages&view=template&type=${docType}`,
        '_blank',
      );
    }
  };

  return (
    <Modal
      open={isOpen}
      footer={null}
      onCancel={onClose}
      centered
      width={560}
      closeIcon={null}
      rootClassName="send-final-offer-modal"
      className="send-final-offer-modal"
      styles={{
        mask: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(4, 5, 27, 0.75)',
        },
        container: {
          background: '#060821',
          backgroundColor: '#060821',
          border: '1px solid #1b1c2f',
          borderRadius: '20px',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.75)',
          padding: '24px',
          color: '#ffffff',
          overflow: 'visible',
        },
        body: {
          padding: 0,
          background: 'transparent',
        },
      }}
    >
      <div className="send-final-offer-content">
        {/* Modal Header */}
        <div className="modal-top-bar">
          <h2 className="modal-heading">{'Send final offer'}</h2>
          <button type="button" className="close-btn" onClick={onClose} aria-label="Close modal">
            <CloseIcon />
          </button>
        </div>

        {/* Offer Summary Card */}
        <div className="offer-summary-card">
          {/* Header Row: Package Selector Dropdown & Price */}
          <div className="card-header-row">
            <div className="dropdown-container" ref={dropdownRef}>
              <div
                className="selected-package-trigger"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <span className="selected-pkg-name">{pkgName}</span>
                <DropdownIcon className={`chevron-svg ${isDropdownOpen ? 'open' : ''}`} />
              </div>

              {isDropdownOpen && (
                <div className="packages-select-menu">
                  {publishedPackages.length === 0 ? (
                    <div className="empty-pkg-opt">{'No published packages'}</div>
                  ) : (
                    publishedPackages.map((pkg) => (
                      <div
                        key={pkg.id}
                        className={`pkg-select-option ${selectedPackage?.id === pkg.id ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedPackage(pkg);
                          setIsDropdownOpen(false);
                        }}
                      >
                        <span>{pkg.name}</span>
                      </div>
                    ))
                  )}
                </div>
              )}

              <div className="pkg-duration-subtitle">
                <span>{`${duration} • 28 Sep - 25 Oct`}</span>
              </div>
            </div>

            <div className="header-price-col">
              <span className="price-tag">{`${currencySymbol}${price}`}</span>
            </div>
          </div>

          {/* Program Start Date Row */}
          <div className="program-start-date-box">
            <div className="start-date-left">
              <div className="cal-icon-wrap">
                <CalendarPeriodIcon />
              </div>
              <div className="date-text-col">
                <span className="date-title">{'Program Start Date'}</span>
                <span className="date-desc">{'Training program will be ready on this date'}</span>
              </div>
            </div>
            <div className="start-date-val">{startDate}</div>
          </div>

          {/* What's Included Section */}
          <div className="whats-included-section">
            <div className="section-title-row">
              <div className="title-icon-wrap">
                <WhatsIncludedIcon />
              </div>
              <span className="section-label">{"What's included"}</span>
            </div>

            <div className="checklist-items">
              {DEFAULT_WHATS_INCLUDED.map((item, idx) => (
                <div key={idx} className="checklist-row">
                  <div className="orange-check-icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <circle cx="9" cy="9" r="9" fill="#261e24" />
                      <path
                        d="M5.5 9.2L7.8 11.5L12.5 6.5"
                        stroke="#FFA101"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="check-text">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Attached Documents Section */}
          <div className="attached-docs-section">
            <span className="docs-section-label">{'Attached documents'}</span>
            <div className="docs-list">
              <div className="doc-item-row" onClick={() => handleDocClick('checkin_policy')}>
                <div className="doc-item-left">
                  <AttachedDocsIcon className="doc-svg-icon" />
                  <span>{'Check-in policy'}</span>
                </div>
                <ChevronRightIcon className="doc-chevron" />
              </div>

              <div className="doc-item-row" onClick={() => handleDocClick('contractual_terms')}>
                <div className="doc-item-left">
                  <AttachedDocsIcon className="doc-svg-icon" />
                  <span>{'Contractual terms'}</span>
                </div>
                <ChevronRightIcon className="doc-chevron" />
              </div>

              <div className="doc-item-row" onClick={() => handleDocClick('cancellation_policy')}>
                <div className="doc-item-left">
                  <AttachedDocsIcon className="doc-svg-icon" />
                  <span>{'Cancellation and unused policy'}</span>
                </div>
                <ChevronRightIcon className="doc-chevron" />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="button"
            className="btn-confirm-send-offer"
            onClick={handleSend}
            disabled={!selectedPackage || isSending}
          >
            <SendOfferIcon className="send-svg-icon" />
            <span>{isSending ? 'Sending Offer...' : 'Send Offer'}</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};
